import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Play, Trash2, ExternalLink, Settings2, Copy, Layers, Search, ChevronDown, ChevronRight, Loader2, ListChecks, Power, X, History } from 'lucide-react';
import GenerateModal from '../components/feeds/GenerateModal';
import AppLayout from '../components/AppLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { StatusBadge, QualityBar, FormatBadge, fmtDate, fmtNum, fmtDur } from '../components/feeds/shared';
import RecordModal from '../components/RecordModal';
import Flag from '../components/Flag';
import usePollWhileActive from '../hooks/usePollWhileActive';
import { api } from '../api/feedy';

const blankFeed = { name: '', channel: 'google_shopping', country: '', currency: 'EUR', output_format: 'xml', store_url: '' };

// One field's match rate. Click to expand the full list of mismatching items
// (ours vs ref) — fetched on demand from the backend, since the stored
// scorecard only keeps a few samples. Each item id opens the record modal.
const MISMATCH_LIMIT = 500;
function FieldRow({ feedId, field, v, onPickItem }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null); // full mismatch report for this field
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const color = v.pct >= 90 ? 'text-emerald-600' : v.pct >= 70 ? 'text-amber-600' : 'text-rose-600';
  const bar = v.pct >= 90 ? 'bg-emerald-500' : v.pct >= 70 ? 'bg-amber-500' : 'bg-rose-500';
  const mismatchCount = (v.total ?? 0) - (v.match ?? 0);
  const expandable = mismatchCount > 0;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data && !loading && feedId) {
      setLoading(true); setError(null);
      api.feeds.mismatches(feedId, field, { limit: MISMATCH_LIMIT })
        .then(setData)
        .catch(() => setError('Could not load mismatches.'))
        .finally(() => setLoading(false));
    }
  };

  // Fall back to the few stored samples until the full list arrives.
  const rows = data?.mismatches || v.samples || [];
  return (
    <div className="rounded-md border border-border">
      <button onClick={() => expandable && toggle()} className="flex w-full items-center gap-3 px-3 py-1.5 text-left disabled:cursor-default" disabled={!expandable}>
        {expandable
          ? (open ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />)
          : <span className="w-3.5 shrink-0" />}
        <code className="w-52 shrink-0 truncate text-[12px]">{field}</code>
        <div className="h-1.5 flex-1 rounded-full bg-muted"><div className={`h-1.5 rounded-full ${bar}`} style={{ width: `${v.pct}%` }} /></div>
        {expandable && <span className="tnum shrink-0 text-[11px] text-muted-foreground">{fmtNum(mismatchCount)} off</span>}
        <span className={`tnum w-12 text-right text-[12px] font-medium ${color}`}>{v.pct}%</span>
      </button>
      {open && expandable && (
        <div className="border-t border-border bg-muted/30 px-3 py-2">
          {loading && rows.length === 0 && <p className="py-2 text-center text-[11px] text-muted-foreground">Loading mismatching items…</p>}
          {error && <p className="py-2 text-center text-[11px] text-rose-600">{error}</p>}
          {rows.length > 0 && (
            <>
              <div className="mb-1 flex items-center justify-between text-[10px] text-muted-foreground">
                <span>Click an id to inspect the full record</span>
                {data && <span className="tnum">{fmtNum(data.mismatch_count)} mismatch{data.mismatch_count === 1 ? '' : 'es'}{data.mismatch_count > rows.length ? ` · showing first ${rows.length}` : ''}</span>}
              </div>
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full table-fixed text-[11px]">
                  <thead>
                    <tr className="bg-muted/60 text-left text-muted-foreground">
                      <th className="w-32 px-2 py-1 font-medium">id</th>
                      <th className="px-2 py-1 font-medium">ours</th>
                      <th className="px-2 py-1 font-medium">ref</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s, i) => (
                      <tr key={i} className="border-t border-border/60 align-top">
                        <td className="px-2 py-1">
                          <button onClick={() => onPickItem(s.id)} className="font-mono text-sky-600 hover:underline" title="Open record">{s.id}</button>
                        </td>
                        <td className="px-2 py-1 font-mono text-rose-600 break-words">{s.ours || '∅'}</td>
                        <td className="px-2 py-1 font-mono text-emerald-700 break-words">{s.ref || '∅'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function Feeds() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [feeds, setFeeds] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [gen, setGen] = useState(null); // { feed } currently generating, shown in GenerateModal
  const [genOpen, setGenOpen] = useState(false);
  const [genTrigger, setGenTrigger] = useState(0);
  const [q, setQ] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // active | inactive | all
  const [mcFilter, setMcFilter] = useState('all'); // all | in | out (Google Merchant Center)
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [cmp, setCmp] = useState(null); // { feed, report?, loading?, busy? }
  const [rec, setRec] = useState(null); // record shown in the shared RecordModal
  const [recLoading, setRecLoading] = useState(false);

  // Open the rich record modal for a mismatching item id (from the compare dialog).
  const openRecord = async (itemId) => {
    if (!cmp?.feed) return;
    setRecLoading(true);
    setRec({ id: itemId }); // show the modal immediately with the id
    try {
      const res = await api.feeds.preview(cmp.feed.id, { q: String(itemId), limit: 1 });
      const row = (res.rows || []).find((r) => String(r.id) === String(itemId)) || res.rows?.[0];
      setRec(row || { id: itemId });
    } catch {
      setRec({ id: itemId });
    } finally {
      setRecLoading(false);
    }
  };

  const openCompare = async (f) => {
    setCmp({ feed: f, loading: true });
    try {
      const list = await api.feeds.comparisons(f.id);
      setCmp({ feed: f, report: list[0]?.report, at: list[0]?.created_at });
    } catch { setCmp({ feed: f, report: null }); }
  };
  const recompare = async () => {
    if (!cmp?.feed) return;
    setCmp((c) => ({ ...c, busy: true }));
    try {
      const c = await api.feeds.compare(cmp.feed.id, {});
      setCmp({ feed: cmp.feed, report: c.report, at: c.created_at });
      load();
    } catch { setCmp((c) => ({ ...c, busy: false })); }
  };
  const [qual, setQual] = useState(null); // { feed, report? }
  const [hist, setHist] = useState(null); // { feed, runs? } generation run history
  const openQuality = async (f) => {
    setQual({ feed: f });
    try { const runs = await api.feeds.runs(f.id); setQual({ feed: f, report: runs[0]?.report || null }); }
    catch { setQual({ feed: f, report: null }); }
  };
  const openHistory = async (f) => {
    setHist({ feed: f });
    try { const runs = await api.feeds.runs(f.id); setHist({ feed: f, runs }); }
    catch { setHist({ feed: f, runs: [] }); }
  };
  // `soft` refetches without flipping the page spinner — used by the live poll
  // so a running feed flips to success/error in place, no flicker.
  const load = (soft = false) => {
    if (!soft) setLoading(true);
    return api.feeds.list().then(setFeeds).finally(() => { if (!soft) setLoading(false); });
  };
  useEffect(() => {
    load();
    api.channels.list().then(setChannels).catch(() => {});
  }, []);

  // While any feed is generating, poll so its status updates on its own.
  const anyRunning = useMemo(() => feeds.some((f) => f.status === 'running'), [feeds]);
  usePollWhileActive(anyRunning, () => load(true));
  useEffect(() => {
    if (params.get('new') === '1') { setCreating({ ...blankFeed }); setParams({}, { replace: true }); }
  }, [params, setParams]);

  const templates = feeds.filter((f) => f.is_template);
  const instances = useMemo(() => {
    let list = feeds.filter((f) => !f.is_template);
    if (statusFilter === 'active') list = list.filter((f) => f.enabled);
    else if (statusFilter === 'inactive') list = list.filter((f) => !f.enabled);
    if (mcFilter === 'in') list = list.filter((f) => f.merchant_center?.length > 0);
    else if (mcFilter === 'out') list = list.filter((f) => !(f.merchant_center?.length > 0));
    if (channelFilter !== 'all') list = list.filter((f) => f.channel === channelFilter);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((f) => (f.name + ' ' + (f.country || '')).toLowerCase().includes(n));
    }
    return list;
  }, [feeds, q, channelFilter, statusFilter, mcFilter]);
  const disabledCount = useMemo(() => feeds.filter((f) => !f.is_template && !f.enabled).length, [feeds]);

  // One flat list sorted by country (then language, name): the flag + code on
  // each row replaces the old per-country group rows.
  const sorted = useMemo(() => [...instances].sort((a, b) =>
    (a.country || '~').localeCompare(b.country || '~')
    || (a.language || 'en').localeCompare(b.language || 'en')
    || a.name.localeCompare(b.name)), [instances]);

  // --- bulk select + generate (only enabled feeds are generatable) ---
  const selectableIds = useMemo(() => instances.filter((f) => f.enabled).map((f) => f.id), [instances]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelected((s) => {
    const n = new Set(s);
    if (selectableIds.every((id) => n.has(id))) selectableIds.forEach((id) => n.delete(id));
    else selectableIds.forEach((id) => n.add(id));
    return n;
  });
  const exitBulk = () => { setBulkMode(false); setSelected(new Set()); };
  const runBulk = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      await api.feeds.bulkGenerate(ids);
      setSelected(new Set());
      load(true); // they flip to 'running'; the live poll takes over
    } finally { setBulkBusy(false); }
  };
  const toggleEnabled = async (f) => {
    await api.feeds.update(f.id, { enabled: !f.enabled });
    load(true);
  };
  const colCount = bulkMode ? 10 : 9;

  const create = async () => {
    setSaving(true);
    try {
      const ch = channels.find((c) => c.key === creating.channel);
      const feed = await api.feeds.create({ ...creating, output_format: ch?.default_format || creating.output_format });
      navigate(`/feeds/${feed.id}`);
    } finally { setSaving(false); }
  };
  const generate = (f) => { setGen({ feed: f }); setBusyId(f.id); setGenTrigger((t) => t + 1); setGenOpen(true); };
  const clone = async (f) => {
    const name = window.prompt('Name for the cloned feed:', `${f.name} copy`);
    if (!name) return;
    const nf = await api.feeds.clone(f.id, { name });
    navigate(`/feeds/${nf.id}`);
  };
  const remove = async (f) => { if (window.confirm(`Delete feed "${f.name}"?`)) { await api.feeds.remove(f.id); load(); } };

  return (
    <AppLayout
      title="Feeds"
      description="Output feeds for Google Shopping & bol.com"
      actions={<Button className="gap-1.5" onClick={() => setCreating({ ...blankFeed })}><Plus className="h-4 w-4" /> New feed</Button>}
    >
      {templates.length > 0 && (
        <Card className="mb-5">
          <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Templates</h2>
            <span className="font-mono text-[11px] text-muted-foreground">clone to create country feeds</span>
          </div>
          <div className="divide-y divide-border">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-4 py-2.5">
                <button onClick={() => navigate(`/feeds/${t.id}`)} className="flex items-center gap-2 text-left">
                  <Badge variant="accent">template</Badge>
                  <span className="text-sm font-medium hover:underline">{t.name}</span>
                </button>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => clone(t)}><Copy className="h-3.5 w-3.5" /> Clone</Button>
                  <Button variant="ghost" size="icon" onClick={() => navigate(`/feeds/${t.id}`)} aria-label="Edit"><Settings2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2.5">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter feeds…" className="h-9 pl-8" />
          </div>
          <Select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)} className="h-9 w-auto min-w-[140px]">
            <option value="all">All channels</option>
            {channels.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </Select>
          <Select value={mcFilter} onChange={(e) => setMcFilter(e.target.value)} className="h-9 w-auto min-w-[150px]" title="Feeds Google Merchant Center fetches">
            <option value="all">All feeds</option>
            <option value="in">In Merchant Center</option>
            <option value="out">Not in Merchant Center</option>
          </Select>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 w-auto min-w-[120px]" title="Only active feeds run on the schedule">
            <option value="active">Active</option>
            <option value="inactive">Disabled{disabledCount ? ` (${disabledCount})` : ''}</option>
            <option value="all">All</option>
          </Select>
          <Button variant={bulkMode ? 'default' : 'outline'} size="sm" className="h-9 gap-1.5" onClick={() => (bulkMode ? exitBulk() : setBulkMode(true))}>
            <ListChecks className="h-4 w-4" /> {bulkMode ? 'Done' : 'Select'}
          </Button>
          <span className="px-1 font-mono text-[11px] text-muted-foreground tnum">{instances.length} feeds</span>
          {anyRunning && (
            <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-amber-600" title="A feed is generating — updating automatically">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" /> live
            </span>
          )}
        </div>
        {bulkMode && (
          <div className="flex flex-wrap items-center gap-3 border-b border-border bg-primary/5 px-3 py-2">
            <span className="font-mono text-[12px] text-muted-foreground tnum">{selected.size} selected</span>
            <Button size="sm" className="h-8 gap-1.5" disabled={!selected.size || bulkBusy} onClick={runBulk}>
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              Generate {selected.size || ''} selected
            </Button>
            {selected.size > 0 && (
              <Button size="sm" variant="ghost" className="h-8 gap-1.5 text-muted-foreground" onClick={() => setSelected(new Set())}>
                <X className="h-3.5 w-3.5" /> Clear
              </Button>
            )}
            <span className="text-[11px] text-muted-foreground">Only active feeds can be generated.</span>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              {bulkMode && (
                <TableHead className="w-9">
                  <input type="checkbox" className="h-3.5 w-3.5 cursor-pointer accent-primary" checked={allSelected} onChange={toggleSelectAll} title="Select all active feeds" />
                </TableHead>
              )}
              <TableHead>Feed</TableHead>
              <TableHead className="w-[1%] whitespace-nowrap">Google MC</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead className="text-right">Products</TableHead>
              <TableHead>Quality</TableHead>
              <TableHead>Match vs ref</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Generated</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={colCount} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>}
            {!loading && instances.length === 0 && (
              <TableRow><TableCell colSpan={colCount} className="py-10 text-center text-muted-foreground">No feeds match.</TableCell></TableRow>
            )}
            {sorted.map((f) => (
              <TableRow key={f.id} className={!f.enabled ? 'opacity-60' : undefined}>
                {bulkMode && (
                  <TableCell className="w-9">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-40"
                      checked={selected.has(f.id)}
                      disabled={!f.enabled}
                      onChange={() => toggleSelect(f.id)}
                      title={f.enabled ? 'Select' : 'Disabled feeds can’t be generated'}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex w-12 shrink-0 items-center gap-1 font-mono text-[11px] text-muted-foreground" title={f.country || 'No country'}>
                      <Flag code={f.country} className="text-sm" />{f.country || '—'}
                    </span>
                    <button className="font-medium hover:underline" onClick={() => navigate(`/feeds/${f.id}`)}>{f.name}</button>
                    {!f.enabled && <span className="rounded bg-rose-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase text-rose-600" title="Disabled — excluded from the nightly run">disabled</span>}
                    <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase text-muted-foreground" title="Language">{f.language || 'en'}</span>
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {f.merchant_center?.length > 0 ? (
                    <Badge
                      variant="success"
                      title={'Used by Google Merchant Center: ' + f.merchant_center.map((m) => `${m.name} (${m.feed_label}/${m.language})${m.prices_linked ? '' : ' — prices NOT linked to Pricython'}`).join(', ')}
                    >
                      In MC
                    </Badge>
                  ) : <span className="text-[12px] text-muted-foreground">—</span>}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[12px] text-muted-foreground">{f.channel}</span>
                    <FormatBadge format={f.output_format} />
                  </div>
                </TableCell>
                <TableCell className="tnum text-right text-muted-foreground">{f.stats?.items_out != null ? fmtNum(f.stats.items_out) : '—'}</TableCell>
                <TableCell>
                  {f.stats?.quality_score != null ? (
                    <button onClick={() => openQuality(f)} title="Click to see quality issues" className="block w-full text-left hover:opacity-75"><QualityBar score={f.stats.quality_score} /></button>
                  ) : <QualityBar score={f.stats?.quality_score} />}
                </TableCell>
                <TableCell>
                  {f.validation ? (
                    <button
                      onClick={() => openCompare(f)}
                      title="Click to see where it differs"
                      className={
                        'tnum text-[12px] font-medium underline-offset-2 hover:underline ' +
                        (f.validation.overall >= 90 ? 'text-emerald-600'
                          : f.validation.overall >= 70 ? 'text-amber-600' : 'text-rose-600')
                      }
                    >
                      {f.validation.overall}%
                    </button>
                  ) : (
                    <span className="text-[12px] text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>{f.enabled ? <StatusBadge status={f.status} /> : <span className="text-[11px] font-medium text-muted-foreground">won’t run</span>}</TableCell>
                <TableCell className="text-[12px] text-muted-foreground">
                  {fmtDate(f.last_generated_at) || 'never'}
                  {f.stats?.duration_s != null && <span className="text-muted-foreground/55"> · {fmtDur(f.stats.duration_s)}</span>}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-0.5">
                    <Button variant="ghost" size="icon" onClick={() => toggleEnabled(f)} aria-label={f.enabled ? 'Disable' : 'Enable'} title={f.enabled ? 'Disable — exclude from runs' : 'Enable'}>
                      <Power className={`h-4 w-4 ${f.enabled ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                    </Button>
                    {f.last_generated_at && (
                      <a href={f.output_url} target="_blank" rel="noreferrer">
                        <Button variant="ghost" size="icon" aria-label="Open output"><ExternalLink className="h-4 w-4" /></Button>
                      </a>
                    )}
                    <Button variant="ghost" size="icon" disabled={!f.enabled} onClick={() => (busyId === f.id ? (setGen({ feed: f }), setGenOpen(true)) : generate(f))} aria-label="Generate">
                      {busyId === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openHistory(f)} aria-label="Generation history" title="Generation history"><History className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => clone(f)} aria-label="Clone"><Copy className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => navigate(`/feeds/${f.id}`)} aria-label="Edit"><Settings2 className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(f)} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!creating} onClose={() => setCreating(null)}>
        {creating && (
          <>
            <DialogHeader>
              <DialogTitle>New feed</DialogTitle>
              <DialogDescription>Pick a channel and country. Mapping and rules come next.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} placeholder="Google Shopping BE" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Channel</Label>
                  <Select value={creating.channel} onChange={(e) => setCreating({ ...creating, channel: e.target.value })}>
                    {channels.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Country</Label><Input value={creating.country} onChange={(e) => setCreating({ ...creating, country: e.target.value })} placeholder="BE" /></div>
                <div className="space-y-1.5"><Label>Currency</Label><Input value={creating.currency} onChange={(e) => setCreating({ ...creating, currency: e.target.value })} /></div>
                <div className="space-y-1.5">
                  <Label>Output format</Label>
                  <Select value={creating.output_format} onChange={(e) => setCreating({ ...creating, output_format: e.target.value })}>
                    <option value="xml">XML</option><option value="csv">CSV</option><option value="tsv">TSV</option>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Store URL</Label><Input value={creating.store_url} onChange={(e) => setCreating({ ...creating, store_url: e.target.value })} placeholder="https://lamptwist.be" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreating(null)}>Cancel</Button>
              <Button onClick={create} disabled={saving || !creating.name}>{saving ? 'Creating…' : 'Create & configure'}</Button>
            </DialogFooter>
          </>
        )}
      </Dialog>

      <Dialog open={!!cmp} onClose={() => setCmp(null)}>
        {cmp && (
          <>
            <DialogHeader>
              <DialogTitle>{cmp.feed.name} — vs reference</DialogTitle>
              <DialogDescription>
                {cmp.report
                  ? `${fmtNum(cmp.report.items_both)} of ${fmtNum(cmp.report.items_ref)} reference items matched · ${fmtNum(cmp.report.only_ours)} only ours · ${fmtNum(cmp.report.only_ref)} only ref · overall ${cmp.report.overall}%`
                  : 'Loading…'}
              </DialogDescription>
            </DialogHeader>
            {cmp.report ? (
              <div className="max-h-[55vh] space-y-1 overflow-y-auto pr-1">
                <p className="px-1 pb-1 text-[11px] text-muted-foreground">Worst-matching fields first · click a row for example mismatches</p>
                {Object.entries(cmp.report.fields)
                  .sort((a, b) => a[1].pct - b[1].pct)
                  .map(([f, v]) => <FieldRow key={f} feedId={cmp.feed.id} field={f} v={v} onPickItem={openRecord} />)}
              </div>
            ) : (
              <div className="py-6 text-center text-sm text-muted-foreground">Loading comparison…</div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCmp(null)}>Close</Button>
              <Button onClick={recompare} disabled={cmp.busy}>{cmp.busy ? 'Re-comparing…' : 'Re-compare now'}</Button>
            </DialogFooter>
          </>
        )}
      </Dialog>

      <Dialog open={!!qual} onClose={() => setQual(null)}>
        {qual && (
          <>
            <DialogHeader>
              <DialogTitle>{qual.feed.name} — quality</DialogTitle>
              <DialogDescription>
                {qual.report
                  ? `score ${qual.report.score}% · ${fmtNum(qual.report.errors)} error checks · ${fmtNum(qual.report.warnings)} warning checks`
                  : qual.report === null ? 'No quality run yet' : 'Loading…'}
              </DialogDescription>
            </DialogHeader>
            {qual.report ? (
              <div className="max-h-[55vh] space-y-1 overflow-y-auto pr-1">
                {(qual.report.issues || []).length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No issues 🎉</p>}
                {(qual.report.issues || []).map((it, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-md border border-border px-3 py-1.5 text-[12px]">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${it.severity === 'error' ? 'bg-rose-500/10 text-rose-600' : 'bg-amber-500/10 text-amber-600'}`}>{it.severity}</span>
                    <code className="shrink-0 text-[11px]">{it.field}</code>
                    <span className="flex-1 truncate text-muted-foreground">{it.message}</span>
                    <span className="tnum shrink-0 font-medium">{fmtNum(it.count)}</span>
                  </div>
                ))}
              </div>
            ) : qual.report === null ? (
              <div className="py-6 text-center text-sm text-muted-foreground">No quality run yet — generate this feed to produce its quality report.</div>
            ) : (
              <div className="py-6 text-center text-sm text-muted-foreground">Loading quality report…</div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setQual(null)}>Close</Button>
            </DialogFooter>
          </>
        )}
      </Dialog>

      <Dialog open={!!hist} onClose={() => setHist(null)} className="max-w-3xl">
        {hist && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="h-4 w-4" /> {hist.feed.name} — generation history
              </DialogTitle>
              <DialogDescription>
                {hist.runs ? `${hist.runs.length} recent run${hist.runs.length === 1 ? '' : 's'}` : 'Loading…'}
              </DialogDescription>
            </DialogHeader>
            {!hist.runs ? (
              <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
            ) : hist.runs.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">No generations recorded yet — generate this feed to start its history.</div>
            ) : (
              <div className="max-h-[60vh] overflow-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead className="text-right">Duration</TableHead>
                      <TableHead className="text-right">Items out</TableHead>
                      <TableHead>Quality</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hist.runs.map((r) => {
                      const dur = r.finished_at && r.started_at
                        ? (new Date(r.finished_at) - new Date(r.started_at)) / 1000 : null;
                      return (
                        <TableRow key={r.id}>
                          <TableCell><StatusBadge status={r.status} /></TableCell>
                          <TableCell className="text-muted-foreground">{fmtDate(r.started_at) || '—'}</TableCell>
                          <TableCell className="text-right tnum text-muted-foreground">{dur != null && dur >= 0 ? fmtDur(dur) : '—'}</TableCell>
                          <TableCell className="text-right tnum">{r.items_out != null ? fmtNum(r.items_out) : '—'}</TableCell>
                          <TableCell><QualityBar score={r.quality_score} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setHist(null)}>Close</Button>
            </DialogFooter>
          </>
        )}
      </Dialog>

      <RecordModal row={recLoading && !rec?.title ? { ...rec, title: 'Loading record…' } : rec} onClose={() => setRec(null)} />

      {gen && (
        <GenerateModal
          feedId={gen.feed.id}
          feedName={gen.feed.name}
          trigger={genTrigger}
          open={genOpen}
          onClose={() => setGenOpen(false)}
          onStart={() => setBusyId(gen.feed.id)}
          onDone={() => { setBusyId(null); load(); }}
        />
      )}
    </AppLayout>
  );
}
